import type { SourceRange } from "./tokens.js";
import type { LogicalNodeKind } from "./ast.js";

export type WarningKind =
  | "domain-dispersal"
  | "shared-infra-fan-in"
  | "cross-domain-store-access"
  | "style-conflict"
  | "missing-runtime"
  | "missing-realizes"
  | "unresolved-realizes"
  | "invalid-owns"
  | "unassigned-domain"
  | "unassigned-service"
  | "unassigned-client"
  | "unresolved-handles"
  | "unassigned-database"
  | "unassigned-queue"
  | "unassigned-storage"
  | "unassigned-resource"
  | "cross-system-ref-implicit-external"
  | "cross-system-ref-unresolved"
  | "unresolved-edge-endpoint"
  | "edge-endpoint-not-at-scope"
  | "edge-target-ambiguous"
  | "cyclic-dependency"
  | "delivers-target-not-client"
  | "client-capability-duplicate"
  | "tag-not-builtin"
  | "tag-not-applicable"
  | "annotation-not-builtin"
  | "style-tag-selector-not-builtin"
  | "style-annotation-selector-not-builtin"
  | "facet-not-declared"
  | "entity-anchor-collision"
  | "legend-ref-unresolved"
  | "style-column-ignored-non-system-view"
  | "style-invalid-enum-value"
  | "style-invalid-hex-color"
  | "style-missing-length-unit"
  | "style-invalid-length-unit"
  | "style-out-of-range"
  | "style-unknown-property"
  | "style-unknown-icon";

/**
 * Per-kind params shape. Each entry carries only the structured data needed
 * to re-render the warning message in any language; producers never build
 * user-visible strings.
 *
 * Consumers that need a localized string call `renderWarning(w, t)` from
 * `@karasu-tools/i18n`; the structured `Warning` stays language-neutral.
 */
export interface WarningParamsByKind {
  "domain-dispersal": { domainId: string; services: string[] };
  /**
   * Two or more services have a resolved `resource` dependency on the same
   * `database` / `queue` / `storage` node within one system scope. This is
   * the actual "shared store / Database-per-Service smell" fan-in signal —
   * keyed on real sharing, independent of how many files declared the store
   * (cf. `infra-redeclared-across-files`, which keys on declaration
   * redundancy). `[external]` stores are excluded: the smell is about owning
   * a store, not depending on a managed third-party one. Info-register per
   * ADR-1386 (fact, not a defect).
   */
  "shared-infra-fan-in": {
    /** id of the shared infra node (e.g. "OrderDB") */
    infraId: string;
    /** kind of the shared infra node */
    infraKind: "database" | "queue" | "storage";
    /** ids of the services that depend on it (≥ 2) */
    services: string[];
  };
  /**
   * A `usecase` in one `domain` reads/writes an infra leaf (`table` /
   * `queue-item` / `bucket`) whose owning domain is a *different* domain.
   * Ownership is derived from the logical layer: a leaf is owned by every
   * `domain` whose `entity` maps it via `table <InfraId>.<subId>`
   * (ADR-1870). The store is keyed at **leaf granularity**
   * (`infraId.tableId`), not the whole `database`, because sibling tables in
   * one store can belong to different domains. Fires when the accessing domain
   * is not in the owner set — so a single-owner reach-in and a third domain
   * touching a co-owned table are both caught, while the owners of a co-owned
   * table are exempt. `[external]` / `[index]` stores are excluded (symmetric
   * with `shared-infra-fan-in`). Info-register per ADR-1386 (a
   * boundary-crossing *fact* some schools call a smell, not a defect). Paired
   * with but orthogonal to `shared-infra-fan-in` (service-count sharing vs
   * ownership-boundary crossing) — the two fire independently. See
   * `docs/adr/1819-domain-store-ownership-diagnostic.md`.
   */
  "cross-domain-store-access": {
    /** id of the domain whose usecase performs the access */
    accessingDomain: string;
    /** ids of the domains that own the leaf (≥ 1; > 1 when co-owned), sorted */
    owningDomains: string[];
    /** id of the infra block the leaf lives in (e.g. "OrderDB") */
    infraId: string;
    /** kind of that infra block */
    infraKind: "database" | "queue" | "storage";
    /** id of the accessed leaf sub-resource (e.g. "orders") */
    tableId: string;
    /** aggregated CRUD direction of the crossing access(es) */
    mode: "read" | "write" | "readwrite";
  };
  "style-conflict": { selector: string; sheetIndices: number[] };
  "missing-runtime": { nodeId: string };
  "missing-realizes": { nodeId: string };
  "unresolved-realizes": {
    /** id of the deploy node that declared `realizes` */
    deployNodeId: string;
    /** id of the surrounding deploy block */
    deployBlockId: string;
    /** the target id that could not be resolved to any service / domain */
    target: string;
  };
  "invalid-owns": { teamId: string; ownedId: string; ownedKind: LogicalNodeKind };
  "unassigned-domain": { domainId: string; label?: string };
  "unassigned-service": { serviceId: string; label?: string };
  "unassigned-client": { clientId: string; label?: string };
  "unresolved-handles": {
    /** id of the node that declared `handles` */
    nodeId: string;
    /** kind of the declaring node ("client" or "service") */
    nodeKind: "client" | "service";
    /** the domain id that could not be resolved through the expose rule */
    domainId: string;
  };
  "unassigned-database": { databaseId: string; label?: string };
  "unassigned-queue": { queueId: string; label?: string };
  "unassigned-storage": { storageId: string; label?: string };
  "unassigned-resource": { resourceId: string };
  "cross-system-ref-implicit-external": {
    ref: string;
    sourceSystemId: string;
    sourceNodeId: string;
    targetSystemId: string;
  };
  "cross-system-ref-unresolved": { ref: string };
  /**
   * An authored edge references a node id that exists nowhere in the merged
   * model. The edge is dropped during view extraction (the resolved endpoint
   * node is preserved — see §S6 / TPL-2170); this warning surfaces the
   * silent drop. Cross-system dotted refs (`Sys.Svc`) are excluded — those are
   * handled by `cross-system-ref-*`.
   */
  "unresolved-edge-endpoint": { from: string; to: string; unresolvedId: string };
  /**
   * An authored edge names an endpoint that exists in the merged model but is
   * not a peer at the scope where the edge is declared, so the edge is dropped
   * from every view (#2075). The canonical form anchors the edge at its source
   * block (`domain A { -> B }`) or, for a cross-domain entity relation, names
   * the target qualified (`OtherDomain.Entity`).
   *
   * An endpoint is *at scope* when it is in `peers(container)`, computed per
   * node **instance** (not per id — see the detector's comment):
   *
   * - container is a `system` → that block's own children, plus the top-level
   *   orphan `domain`s the drawio exporter splices into the root frame;
   * - container has a declaring parent → the container's own id (the
   *   self-anchored source) plus that parent's children;
   * - container is top-level → its own id plus the other orphans the
   *   `__unassigned__` pseudo-system wraps it with, which is the frame they
   *   are drawn on together (ADR-2223). A top-level block the wrap skips
   *   (a `client`) shares a canvas with nothing, so it has no peers.
   *
   * A **qualified** endpoint (`Sys.Svc`, `Sys.Svc.Domain`, …) is no longer
   * skipped (#2577). It must spell the whole path from a top-level `system`
   * down to the target — the same condition the ghost renderer can satisfy,
   * since a ghost frame is a system. A reference that resolves somewhere but
   * not that way is reported here with `qualified: true`, which selects the
   * "spell it from a system" hint instead of the "anchor the edge at its
   * source" one. One code, two message variants: the mechanism is identical
   * and only the fix spelling differs, which is the split ADR-2075 already
   * decided to absorb in the message.
   *
   * Note this branch does not consult the declaring container — a qualified
   * reference reaches the same set from everywhere. The container-relative
   * question is the bare one.
   *
   * Still skipped rather than reported: an id that resolves nowhere at all
   * (owned by `unresolved-edge-endpoint` for bare refs and
   * `cross-system-ref-unresolved` for qualified ones). One exemption: a
   * `domain`-anchored edge to another bare `domain` renders as a derived
   * implicit service edge, at any nesting distance.
   *
   * Warning register per ADR-1386: an edge the author wrote is silently absent
   * from every diagram, which is a defect rather than a style-school fact.
   */
  "edge-endpoint-not-at-scope": {
    from: string;
    to: string;
    /** the endpoint (`from` or `to`) that is not at scope */
    endpointId: string;
    /** kind of the node `endpointId` resolves to */
    endpointKind: string;
    /** id of the node that contains the endpoint, if any */
    ownerId?: string;
    /** kind of that containing node */
    ownerKind?: string;
    /** id of the block the edge is declared in */
    scopeId: string;
    /** kind of that block */
    scopeKind: string;
    /**
     * Set when the endpoint is a dotted path that does not spell a whole path
     * from a top-level `system`. Selects the "spell it from a system" hint.
     */
    qualified?: true;
    /**
     * Full paths the reference *does* match elsewhere in the model, so the
     * hint can name a reachable spelling. Set with {@link qualified}.
     */
    candidates?: string[];
  };
  /**
   * A qualified edge endpoint resolves, within the declaring scope's reach, to
   * two or more nodes that are **not uniform in (kind, depth)** — the shared
   * #2088 discriminator, so a uniform multi-match stays silent as the
   * intentional broadcast ADR-927 / ADR-1566 legitimize.
   *
   * Bare endpoints draw no ambiguity verdict: they keep ADR-2075's peer
   * binding, where a multi-match is the pre-existing broadcast rather than a
   * new question. A code that fired there would be reporting behaviour this
   * slice did not introduce.
   */
  "edge-target-ambiguous": {
    /** the reference as written (joined) */
    path: string;
    /** each reachable match, by full path, so the author can qualify further */
    candidates: Array<{ kind: string; path: string }>;
  };
  "cyclic-dependency": { cyclePath: string[] };
  "delivers-target-not-client": { serviceId: string; targetId: string };
  /**
   * A `client` declared the same `capability <name>` more than once. The
   * second declaration is a programming mistake (no false positives), so
   * we surface it as a warning rather than silently accepting the
   * duplicate.
   */
  "client-capability-duplicate": { clientId: string; name: string };
  /**
   * A tag name is outside the tool vocabulary (builtin tags plus the
   * system-assigned tags of docs/spec/tags-annotations.md § System-assigned
   * tags). `.krs language v2.0` closes the tag register (ADR-2065, #2677):
   * the name still parses but has no effect. Membership / model-specific
   * labeling belongs to `facet`; a new archetype goes through a
   * builtin-addition request. Deliberately has no suppression condition: a
   * style selector or legend ref proves intent, but intent does not change
   * the outcome. Warning register, TPL-1503 state (2): "warned as unknown".
   */
  "tag-not-builtin": {
    /** id of the node carrying the tag, or `"<from> -> <to>"` for an edge */
    nodeId: string;
    /** the tag name as written, without the `[...]` brackets */
    tag: string;
  };
  /**
   * A **builtin** tag is written on a node kind outside its declared
   * `appliesTo` (#2225) — e.g. `service Api [index]`, where `[index]` applies
   * to `database`. The tag parses, renders no badge and, before this
   * diagnostic, said nothing: TPL-1503's fourth state (accepted, inert,
   * undocumented) in the **kind** dimension, after #2159 closed it in the
   * **name** dimension.
   *
   * Same `warning` register as `tag-not-builtin` deliberately — the author's
   * symptom is identical ("I wrote a tag and nothing happened"), so a
   * different severity would only ask them to learn a distinction that does
   * not help them. Never a parse error: models that are inert today keep
   * parsing (same posture as #2159).
   *
   * Mutually exclusive with `tag-not-builtin` by construction: a name outside
   * the builtin set has no `appliesTo` to violate.
   */
  "tag-not-applicable": {
    /** id of the node carrying the tag, or `"<from> -> <to>"` for an edge */
    nodeId: string;
    /** the tag name as written, without the `[...]` brackets */
    tag: string;
    /** the node kind the tag was written on, or `"edge"` for an edge tag */
    nodeKind: string;
    /** the kinds the tag is declared to apply to, in `reference-data` order */
    appliesTo: string[];
  };
  /**
   * An annotation name is outside the builtin lifecycle vocabulary. Same
   * contract as `tag-not-builtin`: parses, no effect, no suppression
   * condition. A near-miss of a builtin name is not this warning: the parser
   * rejects it as the `annotation-possible-typo` error (#2677).
   */
  "annotation-not-builtin": {
    /** id of the node (or `team`) carrying the annotation */
    nodeId: string;
    /** the annotation name as written, without the `@` sigil */
    annotation: string;
  };
  /**
   * A `.krs.style` **selector** targets a tag name outside the tool vocabulary
   * (#2175, design `tags-and-facets.md` (B8)). The style-side counterpart of
   * `tag-not-builtin`, held back until now because announcing a deprecation
   * without a migration target only tells the author to stop doing the one
   * thing that works. Facet selectors (`[facets=<id>]`) are that target, so
   * this fires from the same release they ship in.
   *
   * In `.krs language v2.0` the rule matches nothing (#2677): the cascade
   * leaves it out. This warning is the only sign the rule is dead, which is
   * why it stays a warning (TPL-1503).
   *
   * Model-side and style-side both fire for one name (`[pci]` on a node and
   * `[pci] { … }` in a sheet), which is intentional: they are two edits the
   * author has to make, and one warning would leave the other site unfound.
   */
  "style-tag-selector-not-builtin": {
    /** the tag name as written in the selector, without the `[...]` brackets */
    tag: string;
    /** the full selector text, so the author can find the rule */
    selector: string;
  };
  /**
   * A `.krs.style` selector targets an annotation name outside the builtin
   * lifecycle vocabulary. Same contract as
   * {@link WarningParamsByKind["style-tag-selector-not-builtin"]}.
   */
  "style-annotation-selector-not-builtin": {
    /** the annotation name as written in the selector, without the `@` sigil */
    annotation: string;
    /** the full selector text, so the author can find the rule */
    selector: string;
  };
  /**
   * A `facets <id>` reference names no declared `facet` block (#2065 Part B).
   *
   * Unlike the near-miss `annotation-possible-typo` check, this one is
   * **complete**: the declarations give the validator a definitive "correct"
   * set, so a typo between two user-defined names is caught as surely as a
   * typo of a builtin. Evaluated on the import-merged model, since the
   * declaration and the reference may live in different files.
   *
   * Warning register, not info: an undeclared facet is a broken reference —
   * a fact with a fix — not a style-school judgement (TPL-1386).
   */
  "facet-not-declared": {
    /**
     * The element carrying the `facets` property. A node's id, or — since edges
     * took the property in #2544 — an edge's canonical base form (`A-->B`),
     * an edge having no id of its own.
     *
     * Named `subject` rather than `nodeId` precisely because it is no longer
     * always a node id: a consumer resolving it through `nodePathIndex` would
     * miss on every edge-sourced warning, and a name that says "node" hides
     * that from the compiler.
     */
    subject: string;
    /** the referenced facet id as written */
    facetId: string;
  };
  /**
   * Two addressable targets in the `entity` deep-link namespace share one id.
   * That namespace is model-wide {all domain ids} ∪ {all entity ids}: a
   * `#krs-entity-<id>` anchor opens a domain's entity view (domain id) or
   * focuses an entity (entity id). When an entity id collides with another
   * entity id (under a different domain) or with a domain id, the anchor
   * resolves ambiguously and the bundled static SVG emits duplicate DOM ids,
   * silently breaking CSS `:target`. Warning register: the model still renders
   * and resolves — only deep-link addressability degrades (warn-don't-error).
   * `domain Billing` + a root `entity Billing` is a natural naming clash, so
   * this is a warning, not an error. See `docs/adr/1870-domain-entity-modeling.md`.
   */
  "entity-anchor-collision": {
    /** The id claimed by more than one target in the entity anchor namespace. */
    id: string;
  };
  /**
   * A `ref` entry inside a `legend` block points at a target
   * (annotation / tag / class / id / type) that does not match anything
   * in the file's nodes or style rules. The renderer skips the entry;
   * the warning surfaces the broken reference so the author can fix it.
   */
  "legend-ref-unresolved": {
    /** "@deprecated" / "[external]" / ".legacy" / "#NodeId" / "service" */
    target: string;
    /** Optional title of the legend block, for context in the message. */
    legendTitle?: string;
  };
  /**
   * A `column` hint was resolved for a node, but the current view is not
   * `system`. Layout hints only apply to system view; the renderer
   * surfaces this so authors who target a deploy / org node by id are
   * not silently surprised.
   */
  "style-column-ignored-non-system-view": {
    nodeId: string;
    /** "deploy" or "org" */
    viewType: "deploy" | "org";
  };
  /**
   * Value-level diagnostics produced by `validateStyleValues` (Phase 3).
   * Surfaced in the App's WarningPanel via the compile pipeline; the
   * LSP path emits the same checks as parser-level Diagnostics in
   * `validateDocument`.
   */
  "style-invalid-enum-value": { property: string; value: string; allowed: string[] };
  "style-invalid-hex-color": { property: string; value: string };
  "style-missing-length-unit": { property: string; value: string; allowedUnits: string[] };
  "style-invalid-length-unit": {
    property: string;
    value: string;
    unit: string;
    allowedUnits: string[];
  };
  "style-out-of-range": { property: string; value: number; min?: number; max?: number };
  "style-unknown-property": { property: string };
  "style-unknown-icon": { property: string; name: string };
}

/**
 * A discriminated union over `kind`. Destructuring by `kind` narrows `params`
 * to the right shape automatically, so consumers get full type safety for
 * each warning variant.
 *
 * Prior shape carried `message: string` + `details: string[]` — both removed
 * as part of the Phase B refactor. See ADR-34.
 */
export type Warning = {
  [K in WarningKind]: {
    kind: K;
    params: WarningParamsByKind[K];
    loc?: SourceRange;
  };
}[WarningKind];

/**
 * Visual register of a Warning. Most kinds render as `warning`; style-school
 * smell detections (per ADR-1386 / TPL-1386) render as `info`
 * — the configuration is a structural fact, not a defect karasu prescribes
 * fixing. The mapping is keyed by `kind` so producers do not need to set
 * severity explicitly.
 */
export type WarningSeverity = "warning" | "info";

const INFO_WARNING_KINDS: ReadonlySet<WarningKind> = new Set<WarningKind>([
  "domain-dispersal",
  // Shared-store fan-in is a style-school smell (Database-per-Service), not a
  // defect karasu prescribes fixing — same register as domain-dispersal
  // (ADR-1386 / TPL-1386).
  "shared-infra-fan-in",
  // Cross-domain store access is a boundary-crossing fact some schools call a
  // smell (legitimate under shared kernel / migrations) — same register.
  "cross-domain-store-access",
  // Pre-existing informational kinds: the UI already rendered these with
  // the ℹ icon via the old `WARNING_ICONS` map; preserve that register.
  "missing-runtime",
  "missing-realizes",
]);

export function warningSeverity(kind: WarningKind): WarningSeverity {
  return INFO_WARNING_KINDS.has(kind) ? "info" : "warning";
}

/**
 * A `Warning` rendered to display strings. Produced by the i18n renderer
 * (`renderWarning` in `@karasu-tools/i18n`) — the structured `Warning`
 * itself stays language-neutral. Defined here so every renderer consumer
 * (app, lsp, cli) shares one type.
 */
export interface FormattedWarning {
  message: string;
  details: string[];
}
